import { type ImportItem, ImportQueue } from "@rhema/client";
import { createContext, type ReactNode, useContext, useEffect, useMemo, useSyncExternalStore } from "react";
import { useClient } from "../session/session";

// The WhatsApp import queue lives above the screens, like calls, so an import keeps going while
// the person moves around the app. It picks up where it stopped after a reload.

const ImportContext = createContext<ImportQueue | null>(null);

export function ImportProvider({ children, queue }: { children: ReactNode; queue?: ImportQueue }) {
  const client = useClient();
  const value = useMemo(() => queue ?? new ImportQueue(client, client.localStore), [client, queue]);
  useEffect(() => {
    void value.restore().catch(() => undefined);
  }, [value]);
  return <ImportContext.Provider value={value}>{children}</ImportContext.Provider>;
}

export function useImportQueue(): ImportQueue {
  const queue = useContext(ImportContext);
  if (!queue) throw new Error("useImportQueue needs ImportProvider");
  return queue;
}

export function useImports() {
  const queue = useImportQueue();
  return useSyncExternalStore(queue.subscribe, queue.getState, queue.getState);
}

/** Imports still on their way: reading, waiting in line or sending. */
export function isActive(item: ImportItem): boolean {
  return item.status === "reading" || item.status === "queued" || item.status === "running";
}

/** How far along an import is, from 0 to 1. */
export function progressOf(item: ImportItem): number {
  if (item.status === "done") return 1;
  return item.total ? item.done / item.total : 0;
}
